/**
 * The schedule's furniture — `docs/new-request.md` §10.1, §10.4, §10.8, §10.9,
 * Boards 7, 8, 13, 15.
 *
 * Bands, grade tags, the Best-time cards, the person chips, the *One · Side
 * by side* switch and the *Someone else* search. Each draws an answer the
 * server or a shared function gave it; none of them works anything out.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  GRADE_LABELS, SEGMENT_LABELS, clock, dayComma, dayShort, reasonWords, type Grade, type Opening,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Avatar } from '../../components/primitives.js'
import { Popover } from '../../components/overlays.js'
import { requestsApi, type Candidate, type SomeoneElse } from '../api.js'
import { useRequestSheet, type PersonRef } from '../sheet-context.js'
import {
  ClockGlyph, ColumnsGlyph, CrossGlyph, InfoGlyph, MoveGlyph, PlusGlyph, SearchGlyph, TickGlyph, TruckGlyph,
  WarnGlyph,
} from './glyphs.js'
import { GRADE_TAG, GRADE_TEXT } from './tones.js'

// ---------------------------------------------------------------------------
// Bands (Board 8)
// ---------------------------------------------------------------------------

export type BandTone = 'amber' | 'red' | 'slate' | 'teal'

const BAND: Record<BandTone, string> = {
  amber: 'border-amber-band-line bg-amber-band-bg text-amber-band-text',
  red: 'border-danger-line bg-danger-bg text-danger',
  slate: 'border-slate-band-line bg-slate-band-bg text-slate-band-text',
  teal: 'border-teal-band-line bg-teal-band-bg text-teal-band-text',
}

const BAND_GLYPH: Record<BandTone, ReactNode> = {
  amber: <WarnGlyph />,
  red: <WarnGlyph />,
  slate: <InfoGlyph />,
  teal: <ClockGlyph />,
}

export function ScheduleBand({ tone, glyph, children }: {
  tone: BandTone
  /** The info circle instead of the triangle — *Nothing fits* is amber news, not a warning. */
  glyph?: ReactNode
  children: ReactNode
}) {
  return (
    <div role="note" className={cn('flex items-start gap-[9px] rounded-card border px-3 py-2.5',
      'text-[12px] leading-[1.45] tabular-nums', BAND[tone])}>
      <span className="mt-px flex">{glyph ?? BAND_GLYPH[tone]}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/** Board 8 D: no Routes key, a quota error or no answer — the whole aside is estimates. */
export function EstimatesBand() {
  return (
    <ScheduleBand tone="slate">
      Drive times are <b>estimates</b> right now — straight-line distance at 30 mph, plus 25%.
      {' They’re shown dashed and say “about”.'}
    </ScheduleBand>
  )
}

// ---------------------------------------------------------------------------
// Buttons drawn on the boards' bands
// ---------------------------------------------------------------------------

export function BandButton({ onClick, children, quiet }: {
  onClick: () => void
  children: ReactNode
  quiet?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-button',
        'text-[12.5px] font-semibold',
        quiet
          ? 'border border-transparent px-2 text-text-muted hover:text-text'
          : 'border border-line bg-surface px-3.5 text-text hover:bg-hover')}
    >
      {children}
    </button>
  )
}

export const MoveButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <BandButton onClick={onClick}><MoveGlyph />{label}</BandButton>
)

// ---------------------------------------------------------------------------
// Grades and Best times (§10.3, §10.4)
// ---------------------------------------------------------------------------

export function GradeTag({ grade, small }: { grade: Grade; small?: boolean }) {
  return (
    <span className={cn('inline-flex items-center whitespace-nowrap rounded-tag px-1.5 font-semibold',
      small ? 'h-[18px] text-[10px]' : 'h-[19px] text-[10.5px]', GRADE_TAG[grade])}>
      {GRADE_LABELS[grade]}
    </span>
  )
}

/** *+5 min*, in the grade's colour. */
export const AddedWords = ({ opening, className }: { opening: Opening; className?: string }) => (
  <span className={cn('whitespace-nowrap text-[11.5px] font-bold', GRADE_TEXT[opening.grade], className)}>
    +{opening.added} min
  </span>
)

const selectedRing = 'border-accent shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'

/**
 * The boards set a 20 px avatar's initials at 10 px; `Avatar` scales them at
 * 0.38 of its size (7.6 px there), so the schedule's small ones say so.
 */
export const SMALL_INITIALS = '!text-[10px]'

/** One of the three Best-time cards (Board 7 A; stacked on an iPad and a phone). */
export function BestTimeCard({ opening, selected, phone, onPick }: {
  opening: Opening
  selected: boolean
  /** The reasons drop their distances on a phone (§10.4). */
  phone?: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      className={cn('flex min-w-0 flex-1 basis-0 flex-col justify-start rounded-[11px] border-[1.5px] bg-surface',
        'px-3 py-2.5 text-left leading-[normal]',
        selected ? selectedRing : 'border-line hover:border-line-strong')}
    >
      <span className="flex items-center gap-1.5">
        <GradeTag grade={opening.grade} />
        <span className="flex-1" />
        <AddedWords opening={opening} />
      </span>
      <b className="mt-1.5 block text-[13.5px] font-bold text-text">
        {dayShort(opening.date)} · {clock(opening.start)}
      </b>
      <span className="mt-0.5 block text-[11px] leading-[1.35] text-text-muted">
        {reasonWords(opening.reason, phone ? 'phone' : 'long')}
      </span>
    </button>
  )
}

/** Side by side's card: who, their grade, when, and the short reason (§10.9). */
export function PersonBestCard({ person, opening, selected, notCovering, phone, onPick }: {
  person: PersonRef
  opening: Opening | null
  selected: boolean
  /** *doesn't cover Hardscape · Residential*, in amber — or null when they do. */
  notCovering: string | null
  phone?: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!opening}
      aria-pressed={selected}
      className={cn('flex min-w-0 flex-1 basis-0 flex-col justify-start rounded-[11px] border-[1.5px] bg-surface',
        'px-[11px] py-[9px] text-left leading-[normal]',
        selected ? selectedRing : 'border-line hover:border-line-strong')}
    >
      <span className="flex items-center gap-[5px]">
        <Avatar name={person.name} size={20} className={SMALL_INITIALS} />
        <b className="text-[12px] font-bold text-text">{person.firstName}</b>
        <span className="flex-1" />
        {opening && <GradeTag grade={opening.grade} small />}
      </span>
      {opening
        ? (
          <>
            <b className="mt-1.5 block text-[13px] font-bold text-text">
              {dayShort(opening.date)} · {clock(opening.start)}
            </b>
            <span className="mt-0.5 flex min-w-0 items-center gap-[5px]">
              <AddedWords opening={opening} />
              <span className="min-w-0 truncate text-[11px] text-text-muted">
                {phone && notCovering ? notCovering : reasonWords(opening.reason, 'short')}
              </span>
            </span>
          </>
        )
        : <span className="mt-1.5 block text-[12px] text-text-muted">Nothing fits</span>}
      {!phone && notCovering && (
        <span className="mt-1 block text-[10.5px] font-semibold text-warn">{notCovering}</span>
      )}
    </button>
  )
}

/** *doesn't cover Hardscape · Residential* — the words for a person outside the coverage. */
export function notCoveringWords(covers: boolean | null, division: string | null,
  segment: keyof typeof SEGMENT_LABELS): string | null {
  if (covers !== false || !division) return null
  return `doesn’t cover ${division} · ${SEGMENT_LABELS[segment]}`
}

// ---------------------------------------------------------------------------
// The header's chips and switch (Board 7)
// ---------------------------------------------------------------------------

export function PersonChip({ person, selected, onClick, onRemove }: {
  person: PersonRef
  selected: boolean
  onClick?: () => void
  /** Side by side: the × that takes them out. */
  onRemove?: () => void
}) {
  return (
    /* The boards size these inside the border (28 + 1.5 + 1.5), as they do the tile below. */
    <span className={cn('box-content inline-flex h-7 items-center gap-[5px] whitespace-nowrap rounded-chip pl-1 pr-[9px]',
      'text-[12px]',
      selected
        ? 'border-[1.5px] border-accent bg-green-band-bg font-bold text-accent-dark'
        : 'border border-line bg-surface font-semibold text-text-body')}>
      <button type="button" onClick={onClick} className="inline-flex items-center gap-[5px]">
        <Avatar name={person.name} size={20} className={SMALL_INITIALS} />
        {person.firstName}
      </button>
      {onRemove && (
        <button type="button" aria-label={`Take ${person.firstName} out`} onClick={onRemove}
          className="inline-flex text-text-muted hover:text-text">
          <CrossGlyph />
        </button>
      )}
    </span>
  )
}

export const DashedChip = ({ label, onClick, chipRef }: {
  label: string
  onClick: () => void
  chipRef?: React.Ref<HTMLButtonElement>
}) => (
  <button
    ref={chipRef}
    type="button"
    onClick={onClick}
    className={cn('box-content inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-chip border border-dashed',
      'border-line-strong px-2.5 text-[12px] font-semibold text-text-muted hover:text-text')}
  >
    <PlusGlyph />{label}
  </button>
)

/** *One · Side by side*. */
export function OneSideSwitch({ mode, onChange }: {
  mode: 'one' | 'side'
  onChange: (mode: 'one' | 'side') => void
}) {
  const item = (value: 'one' | 'side', label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={mode === value}
      onClick={() => onChange(value)}
      className={cn('inline-flex h-[22px] items-center justify-center rounded-button px-3 text-[12px] font-semibold',
        mode === value ? 'bg-surface text-text shadow-[0_1px_2px_rgb(20_20_10/0.12)]' : 'text-text-muted')}
    >
      {label}
    </button>
  )
  return (
    <span role="radiogroup" aria-label="Calendars" className="inline-flex rounded-[10px] bg-line-soft p-[3px]">
      {item('one', 'One')}
      {item('side', 'Side by side')}
    </span>
  )
}

/** The 36 px tile in Side by side's header, its border outside the 36 (38 in all, Board 7 B). */
export const SideTile = () => (
  <span className={cn('box-content inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-card border',
    'border-accent-line bg-accent-soft text-accent-dark')}>
    <ColumnsGlyph />
  </span>
)

// ---------------------------------------------------------------------------
// Someone else (§9.2 step 5, §10.1)
// ---------------------------------------------------------------------------

/**
 * *Someone else* / *Add someone*: anyone who sells in the person's branches,
 * each with why they were not recommended. Choosing one shows their calendar
 * (One) or adds them (Side by side).
 */
export function PersonSearch({ open, anchor, onClose, onPick, exclude, action = 'See calendar' }: {
  open: boolean
  anchor: HTMLElement | null
  onClose: () => void
  onPick: (person: PersonRef) => void
  exclude: string[]
  /** What a row does: *See calendar* (One, Board 6 C) or *Add* (Side by side). */
  action?: string
}) {
  const api = useRequestSheet()
  const [text, setText] = useState('')
  const [found, setFound] = useState<SomeoneElse[]>([])
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => { if (open) { setText(''); window.setTimeout(() => input.current?.focus(), 20) } }, [open])

  /*
   * Esc closes this and nothing else. Natively, on the input, because the
   * schedule's own Esc (which closes the whole aside) and the sheet's are
   * native listeners further up and would otherwise hear it first.
   */
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const node = input.current
    if (!open || !node) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      close.current()
    }
    node.addEventListener('keydown', onKey)
    return () => node.removeEventListener('keydown', onKey)
  }, [open])
  useEffect(() => {
    if (!open) return undefined
    const wanted = text.trim()
    if (!wanted) { setFound([]); return undefined }
    let live = true
    const timer = window.setTimeout(() => {
      requestsApi.someoneElse({
        q: wanted, orgUnitId: api.facts.orgUnitId, division: api.facts.divisionCode,
        segment: api.facts.segment,
      })
        .then((answer) => { if (live) setFound(answer.items) })
        .catch(() => { if (live) setFound([]) })
    }, 150)
    return () => { live = false; window.clearTimeout(timer) }
  }, [open, text, api.facts.orgUnitId, api.facts.divisionCode, api.facts.segment])

  const recommended: PersonRef[] = (api.recommendation?.people ?? [])
    .filter((one) => !exclude.includes(one.id))
    .map((one) => ({ id: one.id, name: one.name, firstName: one.firstName }))
  const rows: { person: PersonRef; why: string }[] = text.trim()
    ? found.filter((one) => !exclude.includes(one.id)).map((one) => ({
      person: { id: one.id, name: one.name, firstName: one.name.split(/\s+/)[0] ?? one.name },
      why: one.why,
    }))
    : recommended.map((person) => ({ person, why: 'Recommended' }))

  /* Board 6 C: the green field, *Salespeople*, a row per person with why, and who is searched. */
  return (
    <Popover open={open} onClose={onClose} anchor={anchor} width={400} label="Someone else">
      <div className="pb-1.5 leading-[normal]">
        <div className="p-2 pb-1">
          <label className={cn('flex h-9 items-center gap-2.5 rounded-[11px] border-[1.5px] border-accent bg-surface px-3.5',
            'shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}>
            <span className="inline-flex text-accent"><SearchGlyph /></span>
            <input
              ref={input}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Search anyone who sells…"
              aria-label="Search anyone who sells"
              className={cn('min-w-0 flex-1 bg-transparent text-[15px] font-medium text-text outline-none',
                'focus-visible:shadow-none placeholder:text-text-faint')}
            />
          </label>
        </div>
        <div className="px-[18px] pb-1 pt-2 text-label uppercase text-text-faint">Salespeople</div>
        <div className="max-h-72 overflow-y-auto">
          {rows.map(({ person, why }) => (
            <button
              key={person.id}
              type="button"
              onClick={() => { onPick(person); onClose() }}
              className="mx-1.5 flex w-[calc(100%-12px)] items-center gap-[11px] rounded-[9px] px-3 py-2 text-left hover:bg-green-band-bg"
            >
              <Avatar name={person.name} size={30} />
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="text-[13px] font-semibold text-text">{person.name}</b>
                <span className="text-[11.5px] text-text-muted">{why}</span>
              </span>
              <span className="whitespace-nowrap text-[11.5px] font-semibold text-accent">{action}</span>
            </button>
          ))}
          {text.trim() && rows.length === 0 && (
            <div className="px-[18px] py-2 text-[12px] text-text-muted">Nobody who sells matches.</div>
          )}
        </div>
        <div className="mt-1 border-t border-line-soft px-3.5 pb-0.5 pt-2 text-[11.5px] text-text-faint">
          anyone with the Sales role in branches you can see
        </div>
      </div>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// Nothing fits (Board 8 C)
// ---------------------------------------------------------------------------

/**
 * Nothing in the strip fits: say so, offer the next recommended person's
 * best time, side by side with them, or this person further out.
 */
export function NothingFits({ person, minutes, next, nextFirst, onPickNext, onSideBySide, onFurther }: {
  person: PersonRef
  minutes: string
  next: Candidate | null
  nextFirst: string
  onPickNext: () => void
  onSideBySide: () => void
  onFurther: () => void
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <ScheduleBand tone="amber" glyph={<InfoGlyph />}>
        <b>Nothing fits in {person.firstName}’s next two weeks</b>
        {` — every opening is too short for ${minutes} with the drives.`}
      </ScheduleBand>
      {next && next.best && (
        <div className="flex items-start gap-3 rounded-[12px] border-[1.5px] border-line bg-surface px-3.5 py-3">
          <Avatar name={next.name} size={36} />
          <div className="flex min-w-0 flex-1 flex-col">
            <b className="text-[14px] font-bold text-text">{next.name}</b>
            {next.reasons[0] && (
              <span className="mt-[3px] flex items-center gap-1.5 text-[12px] text-text-body">
                <span className="inline-flex text-text-muted"><TickGlyph /></span>
                <span>{next.reasons[0]}</span>
              </span>
            )}
          </div>
          <div className="flex flex-col items-end">
            <GradeTag grade={next.best.grade} />
            <button
              type="button"
              onClick={onPickNext}
              className={cn('mt-1.5 inline-flex flex-col items-end rounded-[9px] border border-accent-line',
                'bg-surface px-[9px] py-[5px] hover:bg-green-band-bg')}
            >
              <b className="whitespace-nowrap text-[12.5px] font-bold text-text">
                {dayComma(next.best.date)}, {clock(next.best.start)}
              </b>
              <span className="whitespace-nowrap text-[11px] text-text-muted">+{next.best.added} min drive</span>
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        {next && (
          <BandButton onClick={onSideBySide}>
            <ColumnsGlyph size={14.5} stroke={2} />Side by side with {next.firstName}
          </BandButton>
        )}
        <BandButton quiet onClick={onFurther}>{person.firstName} from {dayShort(nextFirst)}</BandButton>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section labels and the legend
// ---------------------------------------------------------------------------

export const Eyebrow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('text-label uppercase text-text-faint', className)}>{children}</div>
)

export function Legend({ columns = 2 }: { columns?: 1 | 2 | 4 }) {
  const dot = (fill: string, label: string) => (
    <span className="inline-flex items-center gap-[5px] text-[11px] text-text-muted">
      <i className={cn('inline-block h-2 w-2 shrink-0 rounded-full', fill)} />{label}
    </span>
  )
  return (
    <div className={cn(columns === 4 ? 'flex items-center gap-3' : 'grid grid-cols-2 gap-x-2.5 gap-y-1.5')}>
      {dot('bg-accent', 'within 3 mi')}
      {dot('bg-[rgb(var(--status-job-scheduled-fg))]', '3–8 mi')}
      {dot('bg-timeline-hour', 'farther')}
      <span className="inline-flex items-center gap-[5px] text-[11px] text-indigo">
        <TruckGlyph size={12} />drive
      </span>
    </div>
  )
}
